import i18n from '../i18n';
import useHttp from '../hooks/use-http';
import { currencyFormatter } from '../functions/formatters';
import Icon from '../icons/Icon';
import ReviewStars from '../UI/ReviewStars/ReviewStars';
import {
    addProductToCartRequest,
    resolveOptionLineQuantity,
    resolveOptionUnitPrice,
    useCartActions,
} from '../WebsiteComponents/Store/cartUtils';
import CartDrawer from '../WebsiteComponents/Store/CartOverview/CartDrawer';
import QuantityStepper from '../components/QuantityStepper/QuantityStepper';
import CompositionChoiceGroups from '../WebsiteComponents/Store/ProductForm/CompositionChoiceGroups';
import ProductDetailOverviewSkeleton from '../WebsiteComponents/Store/ProductForm/ProductDetailOverviewSkeleton';
import ProductOptionCard from '../WebsiteComponents/Store/ProductForm/ProductOptionCard';
import { syncDetailGallery } from '../WebsiteComponents/Store/ProductForm/productDetailDom';
import ProductVariantSelector from '../WebsiteComponents/Store/ProductVariantSelector/ProductVariantSelector';
import {
    attributeValueToken,
    buildVariantAttributeContext,
    getAllAttributeValueOptions,
    getDefaultVariantSelection,
    getSelectedVariant,
    getStoreVariants,
    isValueAvailableForAttribute,
    resolveAvDimensionId,
    selectVariantAttribute,
} from '../WebsiteComponents/Store/ProductStoreOverview/variantUtils';

export function createPlatform(onDrawer, getProps) {
    return {
        i18n,
        format: { money: currencyFormatter },
        hooks: { useCartActions, useHttp },
        variants: {
            buildVariantAttributeContext,
            getSelectedVariant,
            selectVariantAttribute,
            getStoreVariants,
            resolveAvDimensionId,
            attributeValueToken,
            getAllAttributeValueOptions,
            getDefaultVariantSelection,
            isValueAvailableForAttribute,
        },
        cart: {
            state: { count: 0, lines: [], total: 0 },
            get: async () => ({ count: 0, lines: [], total: 0 }),
            useCartActions,
            resolveOptionLineQuantity,
            resolveOptionUnitPrice,
            openDrawer: (args) => {
                onDrawer?.(args?.cart || true);
                return Promise.resolve({ ok: true });
            },
            add: (args = {}) => addProductToCartRequest({
                product: args.product && typeof args.product === 'object' ? args.product : { id: args.productId },
                quantity: args.quantity || 1,
                variantResult: args.variantResult || null,
                selectedOptions: args.selectedOptions && typeof args.selectedOptions === 'object' ? args.selectedOptions : {},
                compositionSelections: args.compositionSelections || null,
                hasVariants: Boolean(args.hasVariants),
                mainProductId: args.mainProductId || args.productId,
            }),
        },
        catalog: {
            list: async () => {
                const current = getProps?.() || {};
                if (current.initialProduct) return [current.initialProduct];
                if (current.product) return [current.product];
                const response = await fetch('/rest/product/list_store/', { credentials: 'same-origin' });
                const data = await response.json().catch(() => null);
                if (!response.ok) throw new Error(data?.message || 'Catalog request failed.');
                if (Array.isArray(data)) return data;
                if (Array.isArray(data?.results)) return data.results;
                return [];
            },
        },
        product: {
            retrieve: async (id) => {
                const productId = String(id || '');
                if (!productId || productId.length > 100) throw new Error('Invalid product.');
                const response = await fetch('/rest/product/' + encodeURIComponent(productId) + '/retrieve_store/', {
                    credentials: 'same-origin',
                });
                const data = await response.json().catch(() => null);
                if (!response.ok) throw new Error(data?.message || 'Product request failed.');
                return data;
            },
            syncGallery: (images, alt) => {
                syncDetailGallery(images, alt);
                return { ok: true };
            },
        },
        checkout: {
            open: () => {
                window.location.href = '/checkout';
                return Promise.resolve({ mode: 'live' });
            },
        },
        ui: {
            ProductVariantSelector,
            ProductOptionCard,
            CompositionChoiceGroups,
            CartDrawer,
            Icon,
            ReviewStars,
            ProductDetailOverviewSkeleton,
            QuantityStepper,
        },
    };
}

export const defaultPlatform = createPlatform();
